import React from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { MONO_FONT, PAGE_FONT, headCellSx, numCellSx, textCellSx } from "../../components/metrics/metricsUi";
import { isMobile } from "../../globalStyles/mobile";
import { purchaseOrderAnalysisService } from "../../utils/services/purchaseOrderAnalysisService";
import {
  STAGE_LABEL,
  headerDisagrees,
  TIMING_LABEL,
  fmtDay,
  fmtDays,
  fmtShortDay,
  fmtUnits,
  riskColor,
} from "../../utils/purchaseOrderAnalysisPageDefaults";
import { ProgressBar, Stat, eyebrowSx, useLatestRequest } from "./parts";

const PoDrawer = ({ ui, poId, base, onClose, onOpenSku }) => {
  const { colors } = ui;
  const mobile = isMobile();
  const detail = useLatestRequest(
    poId && base.warehouse_id
      ? (signal) => purchaseOrderAnalysisService.fetchPo(poId, base, { signal })
      : null,
    [poId, base.warehouse_id, base.client_id],
  );
  const data = detail.data && detail.data.po?.po_id === poId ? detail.data : null;
  const po = data?.po;

  return (
    <Drawer
      anchor="right"
      open={Boolean(poId)}
      onClose={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 3 }}
      PaperProps={{ sx: { width: mobile ? "100%" : 720, backgroundColor: colors.bgPaper, fontFamily: PAGE_FONT } }}
    >
      <Box sx={{ p: mobile ? 2 : 3, textAlign: "left" }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, mb: 0.4 }}>
              Purchase order{po?.supplier_name ? ` · ${po.supplier_name}` : ""}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, fontFamily: MONO_FONT, color: colors.textPrimary }}>
              {po?.po_no || "…"}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {detail.error ? (
          <Typography sx={{ color: ui.danger, fontSize: 13 }}>{detail.error}</Typography>
        ) : !po ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: colors.textSecondary, fontSize: 13 }}>
            <CircularProgress size={16} /> Loading purchase order…
          </Box>
        ) : (
          <>
            <Box sx={{ ...ui.card, p: 2, display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 1.5, mb: 2 }}>
              <Stat ui={ui} label="Stage" value={STAGE_LABEL[po.stage] || po.stage} sub={headerDisagrees(po.stage, po.status) ? `PO header still says ${po.status}` : null} />
              <Stat ui={ui} label="PO date" value={fmtShortDay(po.po_date)} />
              <Stat
                ui={ui}
                label={po.expected_kind === "dispatch" ? "Promised dispatch" : "Expected"}
                value={po.expected_date ? fmtShortDay(po.expected_date) : "No date"}
              />
              <Stat ui={ui} label="First receipt" value={po.received_date ? fmtShortDay(po.received_date) : "—"} />
            </Box>
            <Box sx={{ mb: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>
                  {po.received_reported
                    ? `${fmtUnits(po.units_received)} of ${fmtUnits(po.units_ordered)} units received · ${fmtUnits(po.units_outstanding)} outstanding`
                    : `${fmtUnits(po.units_ordered)} units ordered · received quantities not reported by the provider`}
                </Typography>
                <Typography sx={{ fontSize: 12.5, color: colors.textSecondary }}>{po.warehouse_names.join(", ")}</Typography>
              </Box>
              {po.received_reported ? <ProgressBar ui={ui} value={po.units_received} total={po.units_ordered} /> : null}
            </Box>

            <Typography sx={{ ...eyebrowSx(colors), mb: 0.75 }}>Lines ({fmtUnits(data.lines.length)})</Typography>
            <Box sx={{ overflowX: "auto", mb: 2.5, maxHeight: 360, overflowY: "auto" }}>
              <Table size="small" stickyHeader sx={{ minWidth: 760, tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: 196 }} />
                  <col style={{ width: 110 }} />
                  {[84, 84, 108, 96, 72, 72].map((width, index) => (
                    <col key={index} style={{ width }} />
                  ))}
                </colgroup>
                <TableHead>
                  <TableRow>
                    {["SKU", "Warehouse", "Ordered", "Received", "Outstanding", "Timing", "On hand", "Days"].map((label, index) => (
                      <TableCell key={label} sx={headCellSx(ui, { textAlign: index >= 2 && index !== 5 ? "right" : "left", whiteSpace: "nowrap", backgroundColor: colors.bgPaper })}>
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.lines.map((line) => (
                    <TableRow
                      key={`${line.sku}-${line.warehouse_id}`}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => onOpenSku(line.sku)}
                    >
                      <TableCell sx={textCellSx(ui)}>
                        <Box sx={{ fontFamily: MONO_FONT, fontSize: 12, fontWeight: 600 }}>{line.sku}</Box>
                        <Box title={line.product_name || ""} sx={{ fontSize: 11, color: colors.textTertiary, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {line.product_name || ""}
                        </Box>
                      </TableCell>
                      <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>{line.warehouse_name || "Unmapped"}</TableCell>
                      <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{fmtUnits(line.ordered)}</TableCell>
                      <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{line.received_reported ? fmtUnits(line.received) : "—"}</TableCell>
                      <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT, fontWeight: line.outstanding ? 700 : 400 })}>{fmtUnits(line.outstanding)}</TableCell>
                      <TableCell sx={textCellSx(ui, { whiteSpace: "nowrap" })}>
                        {line.timing ? `${TIMING_LABEL[line.timing]}${line.days_overdue ? ` ${line.days_overdue}d` : ""}` : "—"}
                      </TableCell>
                      <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT })}>{line.on_hand == null ? "—" : fmtUnits(line.on_hand)}</TableCell>
                      <TableCell sx={numCellSx(ui, { fontFamily: MONO_FONT, whiteSpace: "nowrap" })}>
                        {line.risk ? (
                          <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                            <Box component="span" sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: riskColor(line.risk) }} />
                            {line.risk === "out_of_stock" ? "Out" : fmtDays(line.days_cover)}
                          </Box>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>

            {data.deliveries.length ? (
              <>
                <Typography sx={{ ...eyebrowSx(colors), mb: 0.75 }}>Deliveries</Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2.5 }}>
                  {data.deliveries.map((delivery, index) => (
                    <Box key={index} sx={{ ...ui.card, p: 1.5 }}>
                      <Typography sx={{ fontSize: 12.5, fontWeight: 650, color: colors.textPrimary }}>
                        {[
                          delivery.warehouse_name || "Unmapped location",
                          delivery.status,
                          delivery.expected_date ? `ETA ${fmtDay(delivery.expected_date)}` : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
                        {delivery.received_reported
                          ? `${fmtUnits(delivery.units_received)} of ${fmtUnits(delivery.units_expected)} units received`
                          : `${fmtUnits(delivery.units_expected)} units expected · received not reported`}
                        {delivery.carrier ? ` · ${delivery.carrier}` : ""}
                        {delivery.tracking_number ? ` · ${delivery.tracking_number}` : ""}
                      </Typography>
                      {delivery.receipts.map((receipt, receiptIndex) => (
                        <Typography key={receiptIndex} sx={{ fontSize: 11.5, color: colors.textTertiary }}>
                          Receipt {fmtDay(receipt.date)} · {fmtUnits(receipt.units)} units
                          {receipt.damaged ? ` · ${fmtUnits(receipt.damaged)} damaged` : ""}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Box>
              </>
            ) : null}

            {data.trail.length ? (
              <>
                <Typography sx={{ ...eyebrowSx(colors), mb: 0.75 }}>Status trail</Typography>
                <Box sx={{ borderLeft: `2px solid ${colors.border}`, pl: 1.75, ml: 0.5 }}>
                  {data.trail.map((event, index) => (
                    <Box key={index} sx={{ position: "relative", pb: 1.1 }}>
                      <Box sx={{ position: "absolute", left: -19.5, top: 4, width: 9, height: 9, borderRadius: "50%", backgroundColor: ui.accent, border: `2px solid ${colors.bgPaper}` }} />
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: colors.textPrimary }}>{event.status}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: colors.textTertiary }}>
                        {fmtDay(event.date)}
                        {event.location ? ` · ${event.location}` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : null}
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default PoDrawer;
